import { memo, useState } from 'react'
import { ReadmePreview } from './ReadmePreview'
import { FavoriteButton } from './FavoriteButton'
import { useFavorites } from '../hooks/useFavorites'
import { useI18n } from '../i18n/I18nContext'

interface Repo {
  id: number
  name: string
  full_name: string
  description?: string
  html_url: string
  homepage?: string
  topics: string[]
  language?: string
  stargazers_count: number
  forks_count: number
  archived: boolean
  disabled: boolean
}

interface RepoCardProps {
  repo: Repo
}

export const RepoCard = memo(function RepoCard({ repo }: RepoCardProps) {
  const { t } = useI18n()
  const [showReadme, setShowReadme] = useState(false)
  const { isFavorite, toggleFavorite } = useFavorites()
  const homepage = repo.homepage && repo.homepage.trim().length > 0 ? repo.homepage : undefined
  let domain = ''
  if (homepage) {
    try {
      domain = new URL(homepage).hostname
    } catch {
      domain = ''
    }
  }
  const icon = domain ? `https://icons.duckduckgo.com/ip3/${domain}.ico` : ''

  const [owner, repoName] = repo.full_name.split('/')
  const favorite = isFavorite(repo.id)

  return (
    <>
      <div className="card" data-repo-id={repo.id}>
        <div className="card-header">
          <div className="card-header-main">
            <h3 className="title">
              {icon && (
                <img
                  src={icon}
                  alt=""
                  width={16}
                  height={16}
                  className="repo-icon"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              )}
              <span className="repo-name">{repo.name}</span>
              {homepage && <span className="badge deploy-badge">{t('repo.deployBadge')}</span>}
              {repo.archived && <span className="badge archived-badge">{t('repo.archivedBadge')}</span>}
            </h3>
          </div>
          <FavoriteButton
            isFavorite={favorite}
            onClick={() => toggleFavorite(repo.id, repo.full_name)}
            size="small"
          />
        </div>

        <div className="desc">{repo.description || '—'}</div>

        <div className="meta">
          {repo.language && (
            <span className="meta-item" title={t('repo.meta.languageTitle')}>
              <span className="meta-icon">🧠</span>
              {repo.language}
            </span>
          )}
          <span className="meta-item" title={t('repo.meta.starsTitle')}>
            <span className="meta-icon">⭐</span>
            {repo.stargazers_count.toLocaleString()}
          </span>
          <span className="meta-item" title={t('repo.meta.forksTitle')}>
            <span className="meta-icon">🍴</span>
            {repo.forks_count.toLocaleString()}
          </span>
        </div>

        {repo.topics?.length > 0 && (
          <div className="badges">
            {repo.topics.slice(0, 6).map(tp => (
              <span key={tp} className="badge topic-badge" title={t('repo.topicTitle', { topic: tp })}>
                #{tp}
              </span>
            ))}
            {repo.topics.length > 6 && (
              <span className="badge topic-badge more-topics">
                {t('repo.moreTopics', { n: repo.topics.length - 6 })}
              </span>
            )}
          </div>
        )}

        <div className="links">
          <button
            className="button readme-btn"
            onClick={() => setShowReadme(true)}
            title={t('repo.readme')}
          >
            {t('repo.readme')}
          </button>
          {homepage && (
            <a
              className="button primary"
              href={homepage}
              target="_blank"
              rel="noreferrer"
              title={t('repo.openApp')}
            >
              {t('repo.openApp')}
            </a>
          )}
          <a
            className="button secondary"
            href={repo.html_url}
            target="_blank"
            rel="noreferrer"
            title={t('repo.github')}
          >
            {t('repo.github')}
          </a>
        </div>
      </div>

      {showReadme && (
        <ReadmePreview
          owner={owner}
          repo={repoName}
          onClose={() => setShowReadme(false)}
        />
      )}
    </>
  )
})
